import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { format, formatDistanceToNow } from 'date-fns';
import { BarChart3, Plus, RefreshCw, Send, CheckCircle2, Clock, Pencil, Save, FileText, Sparkles } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────

interface NumbersJson {
  active_users?: number;
  total_users?: number;
  prompts_built?: number;
  est_hours_saved?: number;
  top_maturity_level?: number;
  aha_moments?: number;
}

interface Report {
  id: string;
  org_id: string;
  org_name: string;
  report_period: string;
  report_date: string;
  headline: string | null;
  numbers_json: NumbersJson;
  human_story: string | null;
  gaps_noted: string | null;
  recommendation: string | null;
  status: string;
  sent_at: string | null;
  generated_at: string | null;
  created_at: string;
}

interface OrgStat {
  id: string;
  name: string;
  active_users: number;
  total_users: number;
  scored_30d: number;
  scored_7d: number;
  fsr: number | null;
}

// ── Helpers ───────────────────────────────────────────────────────

function statusBadge(status: string, sentAt: string | null) {
  if (status === 'sent')     return <Badge className="bg-green-500/10 text-green-600 border-green-500/30 text-xs"><Send className="h-3 w-3 mr-1" />Sent{sentAt ? ' ' + format(new Date(sentAt), 'MMM d') : ''}</Badge>;
  if (status === 'reviewed') return <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/30 text-xs"><CheckCircle2 className="h-3 w-3 mr-1" />Reviewed</Badge>;
  return <Badge variant="outline" className="text-xs"><Clock className="h-3 w-3 mr-1" />Draft</Badge>;
}

function buildAutoHeadline(nums: NumbersJson, orgName: string): string {
  const pct = nums.total_users ? Math.round(((nums.active_users ?? 0) / nums.total_users) * 100) : 0;
  return `${nums.active_users ?? 0} of your ${nums.total_users ?? 0} ${orgName} users are now active weekly AI users — ${pct}% activation rate.`;
}

function buildAutoRecommendation(nums: NumbersJson, orgName: string): string {
  const pct = nums.total_users ? Math.round(((nums.active_users ?? 0) / nums.total_users) * 100) : 0;
  if (pct >= 80) return `Momentum is strong — this is the right time to discuss seat expansion. We have a proven activation playbook ready for new users.`;
  if (pct >= 50) return `Solid early results. The remaining ${(nums.total_users ?? 0) - (nums.active_users ?? 0)} inactive users are the next priority — a targeted 1:1 session or role-specific prompt drop has an 80%+ activation rate at this stage.`;
  return `Activation is below target. Recommend a focused re-engagement sprint before the next check-in — identify the top 3 inactive users and run a personal outreach this week.`;
}

// ── Main component ────────────────────────────────────────────────

export default function SuccessReportBuilder() {
  const [reports, setReports]     = useState<Report[]>([]);
  const [orgs, setOrgs]           = useState<OrgStat[]>([]);
  const [loading, setLoading]     = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedReport, setSelectedReport] = useState<Report | null>(null);
  const [editing, setEditing]     = useState(false);
  const [savingReport, setSavingReport] = useState(false);

  // Generate dialog
  const [showGenerate, setShowGenerate] = useState(false);
  const [genOrgId, setGenOrgId]   = useState('');
  const [genPeriod, setGenPeriod] = useState('');

  // Editable fields
  const [editHeadline, setEditHeadline]         = useState('');
  const [editHumanStory, setEditHumanStory]     = useState('');
  const [editGaps, setEditGaps]                 = useState('');
  const [editRecommendation, setEditRecommendation] = useState('');
  const [editNumbers, setEditNumbers]           = useState<NumbersJson>({});

  const load = useCallback(async () => {
    setLoading(true);
    const [repRes, orgRes] = await Promise.all([
      supabase.from('client_success_reports').select('*').order('created_at', { ascending: false }),
      supabase.rpc('get_all_orgs_admin'),
    ]);
    const orgMap: Record<string, string> = {};
    const orgStats: OrgStat[] = (orgRes.data ?? []).map((o: any) => {
      orgMap[o.id] = o.name;
      return {
        id: o.id, name: o.name,
        active_users: o.active_users ?? 0,
        total_users: o.total_users ?? o.seat_count ?? 0,
        scored_30d: o.scored_30d ?? 0,
        scored_7d: o.scored_7d ?? 0,
        fsr: o.fsr ?? null,
      };
    });
    setOrgs(orgStats);
    setReports(
      (repRes.data ?? []).map(r => ({
        ...r,
        org_name: orgMap[r.org_id] ?? '—',
        numbers_json: (r.numbers_json ?? {}) as NumbersJson,
      }))
    );
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openReport = (r: Report) => {
    setSelectedReport(r);
    setEditing(false);
    setEditHeadline(r.headline ?? '');
    setEditHumanStory(r.human_story ?? '');
    setEditGaps(r.gaps_noted ?? '');
    setEditRecommendation(r.recommendation ?? '');
    setEditNumbers(r.numbers_json);
  };

  const generateReport = async () => {
    if (!genOrgId || !genPeriod.trim()) { toast.error('Select an org and enter a period'); return; }
    setGenerating(true);

    const org = orgs.find(o => o.id === genOrgId);
    if (!org) { setGenerating(false); return; }

    const nums: NumbersJson = {
      active_users: org.active_users,
      total_users: org.total_users,
      prompts_built: org.scored_30d,
      est_hours_saved: Math.round((org.scored_30d * 0.25) * 10) / 10,
    };

    const payload = {
      org_id: genOrgId,
      report_period: genPeriod.trim(),
      report_date: format(new Date(), 'yyyy-MM-dd'),
      headline: buildAutoHeadline(nums, org.name),
      numbers_json: nums,
      human_story: '',
      gaps_noted: '',
      recommendation: buildAutoRecommendation(nums, org.name),
      status: 'draft',
      generated_at: new Date().toISOString(),
    };

    // Optionally enrich with AI exec summary
    try {
      const { data: aiData } = await supabase.functions.invoke('generate-exec-summary', {
        body: { org_id: genOrgId, triggered_by: 'success_report' },
      });
      if (aiData?.summary) {
        payload.human_story = aiData.summary;
      }
    } catch {
      // edge function not deployed or not needed — carry on
    }

    const { data: inserted, error } = await supabase
      .from('client_success_reports')
      .insert([payload as any])
      .select()
      .single();

    setGenerating(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Report generated');
    setShowGenerate(false);
    setGenOrgId(''); setGenPeriod('');
    await load();
    if (inserted) openReport({ ...inserted, org_name: org.name, numbers_json: inserted.numbers_json as NumbersJson });
  };

  const saveEdits = async () => {
    if (!selectedReport) return;
    setSavingReport(true);
    const { error } = await supabase
      .from('client_success_reports')
      .update({
        headline: editHeadline.trim() || null,
        human_story: editHumanStory.trim() || null,
        gaps_noted: editGaps.trim() || null,
        recommendation: editRecommendation.trim() || null,
        numbers_json: editNumbers as unknown as import('@/integrations/supabase/types').Json,
      })
      .eq('id', selectedReport.id);
    setSavingReport(false);
    if (error) { toast.error(error.message); return; }
    toast.success('Report saved');
    setEditing(false);
    load();
    setSelectedReport(prev => prev ? {
      ...prev, headline: editHeadline, human_story: editHumanStory,
      gaps_noted: editGaps, recommendation: editRecommendation, numbers_json: editNumbers,
    } : null);
  };

  const advanceStatus = async (id: string, newStatus: string) => {
    const update: Record<string, unknown> = { status: newStatus };
    if (newStatus === 'sent') update.sent_at = new Date().toISOString();
    const { error } = await supabase.from('client_success_reports').update(update).eq('id', id);
    if (error) { toast.error(error.message); return; }
    toast.success(newStatus === 'reviewed' ? 'Marked as reviewed' : 'Report sent');
    load();
    if (selectedReport?.id === id) setSelectedReport(prev => prev ? { ...prev, status: newStatus, sent_at: newStatus === 'sent' ? new Date().toISOString() : prev.sent_at } : null);
  };

  const nums = selectedReport?.numbers_json ?? {};

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-semibold">Client Success Reports</h1>
          <p className="text-sm text-muted-foreground mt-0.5">Auto-generated from platform data — review and send to clients</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={() => setShowGenerate(true)}>
            <Sparkles className="h-3.5 w-3.5 mr-1.5" />Generate Report
          </Button>
        </div>
      </div>

      <div className="flex gap-6">
        {/* Left: list */}
        <div className="w-64 shrink-0 space-y-2">
          {loading ? (
            <p className="text-sm text-muted-foreground text-center py-8">Loading…</p>
          ) : reports.length === 0 ? (
            <div className="text-center py-8">
              <BarChart3 className="h-7 w-7 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">No reports yet</p>
              <Button size="sm" className="mt-3" onClick={() => setShowGenerate(true)}>
                <Plus className="h-3.5 w-3.5 mr-1.5" />Generate
              </Button>
            </div>
          ) : reports.map(r => (
            <Card
              key={r.id}
              className={`cursor-pointer hover:bg-muted/20 transition-colors ${selectedReport?.id === r.id ? 'border-primary' : ''}`}
              onClick={() => openReport(r)}
            >
              <CardContent className="p-3">
                <div className="flex items-start justify-between gap-1 mb-1">
                  <span className="font-medium text-sm leading-tight">{r.org_name}</span>
                  {statusBadge(r.status, r.sent_at)}
                </div>
                <p className="text-xs text-muted-foreground">{r.report_period}</p>
                <p className="text-xs text-muted-foreground">{formatDistanceToNow(new Date(r.created_at), { addSuffix: true })}</p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Right: detail */}
        <div className="flex-1 min-w-0">
          {!selectedReport ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground border border-border/50 rounded-lg">
              <FileText className="h-8 w-8 mb-2" />
              <p className="text-sm">Select a report to view or edit</p>
            </div>
          ) : (
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-base">{selectedReport.org_name} — {selectedReport.report_period}</CardTitle>
                    <div className="flex items-center gap-2 mt-1">
                      {statusBadge(selectedReport.status, selectedReport.sent_at)}
                      <span className="text-xs text-muted-foreground">
                        Generated {selectedReport.generated_at ? formatDistanceToNow(new Date(selectedReport.generated_at), { addSuffix: true }) : '—'}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1.5">
                    {!editing && (
                      <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                        <Pencil className="h-3.5 w-3.5 mr-1.5" />Edit
                      </Button>
                    )}
                    {selectedReport.status === 'draft' && (
                      <Button size="sm" variant="outline" onClick={() => advanceStatus(selectedReport.id, 'reviewed')}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" />Approve
                      </Button>
                    )}
                    {selectedReport.status === 'reviewed' && (
                      <Button size="sm" onClick={() => advanceStatus(selectedReport.id, 'sent')}>
                        <Send className="h-3.5 w-3.5 mr-1.5" />Send to client
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-5">

                {/* Numbers */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">The numbers</p>
                  {editing ? (
                    <div className="grid grid-cols-3 gap-3">
                      {([ ['active_users', 'Active users'], ['total_users', 'Total users'], ['prompts_built', 'Prompts built'], ['est_hours_saved', 'Est. hours saved'], ['top_maturity_level', 'Top maturity level'], ['aha_moments', 'Aha moments'] ] as [keyof NumbersJson, string][]).map(([k, label]) => (
                        <div key={k} className="space-y-1">
                          <Label className="text-xs">{label}</Label>
                          <Input
                            type="number"
                            value={editNumbers[k] ?? ''}
                            onChange={e => setEditNumbers(prev => ({ ...prev, [k]: e.target.value ? Number(e.target.value) : undefined }))}
                          />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3">
                      {[
                        { label: 'Active users',   value: nums.active_users != null ? `${nums.active_users}/${nums.total_users}` : '—' },
                        { label: 'Prompts built',  value: nums.prompts_built ?? '—' },
                        { label: 'Est. hrs saved', value: nums.est_hours_saved != null ? `${nums.est_hours_saved}h` : '—' },
                        { label: 'Top maturity',   value: nums.top_maturity_level != null ? `Level ${nums.top_maturity_level}` : '—' },
                        { label: 'Aha moments',    value: nums.aha_moments ?? '—' },
                        { label: 'Activation',     value: nums.active_users && nums.total_users ? `${Math.round((nums.active_users / nums.total_users) * 100)}%` : '—' },
                      ].map(stat => (
                        <div key={stat.label} className="bg-muted/30 rounded-lg p-3">
                          <p className="text-xs text-muted-foreground mb-0.5">{stat.label}</p>
                          <p className="font-semibold text-sm">{stat.value}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <Separator />

                {/* Headline */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Headline</p>
                  {editing ? (
                    <Textarea rows={2} value={editHeadline} onChange={e => setEditHeadline(e.target.value)} placeholder="The headline clients will read first…" />
                  ) : (
                    <p className="text-sm font-medium">{selectedReport.headline ?? <span className="italic text-muted-foreground">No headline</span>}</p>
                  )}
                </div>

                {/* Human story */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">The human story</p>
                  {editing ? (
                    <Textarea rows={4} value={editHumanStory} onChange={e => setEditHumanStory(e.target.value)} placeholder="Who did something remarkable this month? What changed for them?" />
                  ) : (
                    <p className="text-sm text-muted-foreground">{selectedReport.human_story ?? <span className="italic">None added</span>}</p>
                  )}
                </div>

                {/* Gaps */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">What's not working yet</p>
                  {editing ? (
                    <Textarea rows={2} value={editGaps} onChange={e => setEditGaps(e.target.value)} placeholder="Be honest — what hasn't landed, and what are we doing about it?" />
                  ) : (
                    <p className="text-sm text-muted-foreground">{selectedReport.gaps_noted ?? <span className="italic">None noted</span>}</p>
                  )}
                </div>

                {/* Recommendation */}
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">The recommendation</p>
                  {editing ? (
                    <Textarea rows={3} value={editRecommendation} onChange={e => setEditRecommendation(e.target.value)} placeholder="What should happen next — expand seats, next engagement phase, renewal framing?" />
                  ) : (
                    <p className="text-sm">{selectedReport.recommendation ?? <span className="italic text-muted-foreground">None</span>}</p>
                  )}
                </div>

                {editing && (
                  <div className="flex gap-2 pt-1">
                    <Button size="sm" onClick={saveEdits} disabled={savingReport}>
                      <Save className="h-3.5 w-3.5 mr-1.5" />{savingReport ? 'Saving…' : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Generate dialog */}
      <Dialog open={showGenerate} onOpenChange={setShowGenerate}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader><DialogTitle>Generate Success Report</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-xs">Client org *</Label>
              <Select value={genOrgId} onValueChange={setGenOrgId}>
                <SelectTrigger><SelectValue placeholder="Select org…" /></SelectTrigger>
                <SelectContent>{orgs.map(o => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Report period *</Label>
              <Input value={genPeriod} onChange={e => setGenPeriod(e.target.value)} placeholder="e.g. Month 1, Q1 2026, Week of Mar 18" />
            </div>
            <p className="text-xs text-muted-foreground">
              Cosmo will pull current platform stats for this org and auto-generate the headline, numbers, and recommendation. You can edit everything before sending.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowGenerate(false)}>Cancel</Button>
            <Button onClick={generateReport} disabled={generating || !genOrgId || !genPeriod}>
              {generating ? 'Generating…' : <><Sparkles className="h-3.5 w-3.5 mr-1.5" />Generate</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
